import React, { FC, memo, useCallback, useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Link,
  Typography
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { MainPriceListTable } from './MainPriceListTable';
import { usePriceList } from 'src/hooks/priceList';
import { slices, useAppDispatch } from 'src/redux';
import { unwrapResult } from '@reduxjs/toolkit';
import { useLocation } from 'react-router';
import { isEmpty } from 'lodash';
import { parseSearchV2 } from 'src/utils';

const { actions: productActions } = slices.product;

const useStyles = makeStyles((theme) => ({
  root: {},
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500]
  }
}));

interface Props {
  isOpen: boolean;
  product_ids: string[];
  handleClose: () => void;
}

const Component: FC<Props> = ({ isOpen, product_ids, handleClose }) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { search } = useLocation();
  const [priceListUpdate, setPriceListUpdate] = useState<boolean>(false);

  const {
    poweredBuildsComponentData,
    onLoadMoreItems,
    onUpdatePriceList
  } = usePriceList();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [
    poweredBuildComponentListData,
    setPoweredBuildComponentListData
  ] = useState<any>([]);

  const getPoweredBuildsComponent = useCallback(
    async (product_ids: string[], params: string) => {
      try {
        setIsLoading(true);
        const response = unwrapResult(
          await dispatch(
            productActions?.getProductListFromPoweredBuildsComponentThunk({
              product_ids,
              sort_by_column: params,
              page: 1
            })
          )
        );

        if (response?.success) {
          setPoweredBuildComponentListData(response?.originalData?.data);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    },
    [dispatch]
  );

  useEffect(() => {
    if (product_ids && product_ids?.length > 0) {
      if (priceListUpdate) {
        setPriceListUpdate(false);
        return;
      }
      const payload = isEmpty(search) ? {} : parseSearchV2(search);
      getPoweredBuildsComponent(product_ids, payload.sort_by_column);
    }
  }, [getPoweredBuildsComponent, product_ids, search, priceListUpdate]);

  return (
    <Dialog
      fullWidth
      maxWidth="xl"
      open={isOpen}
      onClose={handleClose}
      aria-labelledby="powered-build-components"
    >
      <DialogTitle disableTypography>
        <Link
          href={`/app/pc-bundles/components/${poweredBuildsComponentData?.bundle_id}`}
          target="_blank"
        >
          <Typography variant="h4">Powered Build Components</Typography>
        </Link>

        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={handleClose}
        >
          <CloseIcon color="secondary" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {isLoading ? (
          <LinearProgress />
        ) : (
          <MainPriceListTable
            isLoading={isLoading}
            priceList={poweredBuildComponentListData}
            isPoweredBuildsTable={false}
            isIncomingPriceListTable={false}
            onUpdatePriceList={(data) => {
              setPriceListUpdate(true);
              onUpdatePriceList(data);
            }}
            onLoadMoreItems={onLoadMoreItems}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const PoweredBuildStocksDialog = memo(Component);
